import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import './HomePageBuilderView.css';

function HomePageBuilderView() {
  const { language } = useLanguage();
  const { data } = useQuery({
    queryKey: ['home-page-builder-public', language],
    queryFn: async () => {
      const res = await apiClient.get('/home-page-builder/public', {
        params: { lang: language || 'lt', _: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      return res.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const sections = data?.content?.sections ?? [];
  if (sections.length === 0) return null;

  // Grupuoti nuoseklius blokus su tuo pačiu rowGroup į vieną eilutę
  const rows = [];
  let i = 0;
  while (i < sections.length) {
    const section = sections[i];
    if (!section.rowGroup) {
      rows.push([section]);
      i += 1;
      continue;
    }
    const groupId = section.rowGroup;
    const row = [];
    while (i < sections.length && sections[i].rowGroup === groupId) {
      row.push(sections[i]);
      i += 1;
    }
    rows.push(row);
  }

  return (
    <div className="home-page-builder-view">
      {rows.map((row, rowIndex) =>
        row.length === 1 ? (
          <BuilderSection key={row[0].id} section={row[0]} />
        ) : (
          <div key={`row-${rowIndex}-${row[0].id}`} className="builder-row">
            {row.map((section) => (
              <BuilderSection key={section.id} section={section} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function BuilderSection({ section }) {
  const { type, settings } = section;
  switch (type) {
    case 'hero': {
      const hasVideo = settings.backgroundVideo && (settings.backgroundVideo.startsWith('http') || settings.backgroundVideo.startsWith('/'));
      const hasImage = settings.backgroundImage && !hasVideo;
      const textAlign = settings.textAlign || 'left';
      const verticalAlign = settings.verticalAlign || 'center';
      const justifyContentMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
      const alignItemsMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
      return (
        <section
          className="builder-hero"
          style={{
            justifyContent: justifyContentMap[textAlign],
            alignItems: alignItemsMap[verticalAlign],
          }}
        >
          {hasVideo && (
            <div className="builder-hero-media builder-hero-video-wrap">
              <video
                className="builder-hero-video"
                src={settings.backgroundVideo}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden
              />
              <div className="builder-hero-overlay" aria-hidden />
            </div>
          )}
          {hasImage && !hasVideo && (
            <div
              className="builder-hero-media builder-hero-image-wrap"
              style={{ backgroundImage: `url(${settings.backgroundImage})` }}
              aria-hidden
            />
          )}
          <div className="builder-hero-inner" style={{ textAlign }}>
            <h1 className="builder-hero-title">{settings.title || ''}</h1>
            {settings.subtitle && <p className="builder-hero-subtitle">{settings.subtitle}</p>}
            {settings.buttonText && settings.buttonLink && (
              <Link to={settings.buttonLink} className="builder-hero-btn">
                {settings.buttonText}
              </Link>
            )}
          </div>
        </section>
      );
    }
    case 'heading': {
      const Tag = settings.tag || 'h2';
      const textAlign = settings.textAlign || 'left';
      return (
        <section className="builder-heading-wrap">
          <div className="builder-container" style={{ textAlign }}>
            <Tag className="builder-heading">{settings.text || ''}</Tag>
          </div>
        </section>
      );
    }
    case 'text': {
      const textAlign = settings.textAlign || 'left';
      return (
        <section className="builder-text-wrap">
          <div className="builder-container" style={{ textAlign }}>
            <div className="builder-text" dangerouslySetInnerHTML={{ __html: (settings.content || '').replace(/\n/g, '<br />') }} />
          </div>
        </section>
      );
    }
    case 'image': {
      const textAlign = settings.textAlign || 'left';
      return (
        <section className="builder-image-wrap">
          <div className="builder-container" style={{ textAlign }}>
            {settings.src ? (
              settings.link ? (
                <Link to={settings.link}>
                  <img src={settings.src} alt={settings.alt || ''} className="builder-image-img" />
                </Link>
              ) : (
                <img src={settings.src} alt={settings.alt || ''} className="builder-image-img" />
              )
            ) : (
              <div className="builder-image-placeholder" title="Redaktoriuje pridėkite nuotraukos URL">
                <span className="builder-image-placeholder-icon">🖼</span>
                <span className="builder-image-placeholder-text">Pridėkite nuotraukos URL (Admin → Pirmasis puslapis)</span>
              </div>
            )}
          </div>
        </section>
      );
    }
    case 'button': {
      const textAlign = settings.textAlign || 'left';
      return (
        <section className="builder-button-wrap">
          <div className="builder-container" style={{ textAlign }}>
            {settings.link && (
              <Link to={settings.link} className="builder-button">
                {settings.text || 'Mygtukas'}
              </Link>
            )}
          </div>
        </section>
      );
    }
    case 'toggle': {
      const summaryText = settings.title || '';
      const content = (settings.content || '').replace(/\n/g, '<br />');
      return (
        <section className="builder-toggle-wrap">
          <div className="builder-container">
            <details className="builder-toggle-details" open={!!settings.openByDefault}>
              <summary>{summaryText || '…'}</summary>
              <div
                className="builder-toggle-inner"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </details>
          </div>
        </section>
      );
    }
    case 'spacer':
      return (
        <div
          className="builder-spacer"
          style={{ height: `${settings.height ?? 40}px` }}
        />
      );
    case 'products_grid':
      return <BuilderProductsGrid title={settings.title} limit={settings.limit ?? 8} />;
    case 'feature': {
      const align = settings.align || 'left';
      const textBlock = (
        <div className="builder-feature-content">
          {settings.title && <h2 className="builder-feature-title">{settings.title}</h2>}
          {settings.text && (
            <div className="builder-feature-text" dangerouslySetInnerHTML={{ __html: (settings.text || '').replace(/\n/g, '<br />') }} />
          )}
        </div>
      );
      const imageBlock = (
        <div className="builder-feature-image-wrap">
          {settings.imageSrc ? (
            <img src={settings.imageSrc} alt={settings.imageAlt || settings.title || ''} className="builder-feature-image" />
          ) : (
            <div className="builder-feature-image-placeholder" />
          )}
        </div>
      );
      return (
        <section className={`builder-feature-wrap builder-feature-align-${align}`}>
          <div className="builder-feature-inner">
            {align === 'right' ? (
              <>
                {imageBlock}
                {textBlock}
              </>
            ) : (
              <>
                {textBlock}
                {imageBlock}
              </>
            )}
          </div>
        </section>
      );
    }
    case 'elite_section':
      return (
        <section className="builder-elite-wrap">
          <div className="builder-container">
            {settings.title && <h2 className="builder-elite-title">{settings.title}</h2>}
            {settings.text && (
              <div className="builder-elite-text" dangerouslySetInnerHTML={{ __html: (settings.text || '').replace(/\n/g, '<br />') }} />
            )}
            {settings.buttonText && settings.buttonLink && (
              <Link to={settings.buttonLink} className="builder-elite-btn">
                {settings.buttonText}
              </Link>
            )}
            <div className="builder-elite-images">
              {[settings.image1, settings.image2, settings.image3].map((src, i) => (
                <div key={i} className="builder-elite-image-wrap">
                  {src ? (
                    <img src={src} alt="" className="builder-elite-image" />
                  ) : (
                    <div className="builder-elite-image-placeholder" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    default:
      return null;
  }
}

function BuilderProductsGrid({ title, limit }) {
  const { data } = useQuery({
    queryKey: ['builder-products', limit],
    queryFn: async () => {
      const res = await apiClient.get(`/products?limit=${limit}`);
      return res.data;
    },
  });
  const products = data?.products ?? [];

  return (
    <section className="builder-products-wrap">
      <div className="builder-container">
        {title && <h2 className="builder-products-title">{title}</h2>}
        <div className="builder-products-grid">
          {products.map((product) => (
            <Link key={product.id} to={`/products/${product.slug}`} className="builder-product-card">
              <div className="builder-product-image">
                {product.primary_image ? (
                  <img src={product.primary_image} alt={product.name} />
                ) : (
                  <div className="builder-product-placeholder" />
                )}
              </div>
              <h3 className="builder-product-name">{product.name}</h3>
              <p className="builder-product-price">
                {product.price ? parseFloat(product.price).toFixed(2) : '0.00'} €
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// Eksportuojama puslapių turiniui iš blokų (AdminPages redaktorius)
export function BuilderSectionsView({ sections }) {
  if (!sections || sections.length === 0) return null;
  const rows = [];
  let i = 0;
  while (i < sections.length) {
    const section = sections[i];
    if (!section.rowGroup) {
      rows.push([section]);
      i += 1;
      continue;
    }
    const groupId = section.rowGroup;
    const row = [];
    while (i < sections.length && sections[i].rowGroup === groupId) {
      row.push(sections[i]);
      i += 1;
    }
    rows.push(row);
  }
  return (
    <div className="home-page-builder-view">
      {rows.map((row, rowIndex) =>
        row.length === 1 ? (
          <BuilderSection key={row[0].id} section={row[0]} />
        ) : (
          <div key={`row-${rowIndex}-${row[0].id}`} className="builder-row">
            {row.map((section) => (
              <BuilderSection key={section.id} section={section} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default HomePageBuilderView;
